// Must be the FIRST import. Shims NativeModules.RNAWSCognito with a BigInt-based
// implementation so amazon-cognito-identity-js uses fast modPow on Hermes.
import '@/lib/cognito-bigint-shim'

import {
  useFonts,
  Outfit_400Regular,
  Outfit_500Medium,
  Outfit_600SemiBold,
  Outfit_700Bold,
  Outfit_800ExtraBold,
} from '@expo-google-fonts/outfit'
import { Stack, usePathname, useRouter, useSegments } from 'expo-router'
import * as SplashScreen from 'expo-splash-screen'
import { useEffect, useRef } from 'react'
import { Platform, View } from 'react-native'
import PostHogNativeProvider from '@/components/analytics/PostHogNativeProvider'
import AppErrorBoundary from '@/components/AppErrorBoundary'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { I18nProvider, useI18n } from '@/contexts/I18nContext'
import { ThemeProvider, useThemeColors } from '@/contexts/ThemeContext'
import { getPostHogClient, reportError, useAnalytics } from '@/hooks/useAnalytics'

import '../global.css'

// Catch stale chunk references (AsyncRequireError) left over from a deploy that
// happened while the user had the app open. Force a single reload so they pick
// up the current index.html and matching chunk hashes instead of seeing a
// broken screen. Guarded with sessionStorage so we never loop if reloading
// doesn't fix it (e.g. user is offline).
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const message = event?.error?.message || event?.message || ''
    if (message.includes('Loading module') || message.includes('AsyncRequireError')) {
      const key = 'chunk_reload_attempted'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
      }
    }
  })
}

SplashScreen.preventAutoHideAsync()

const PROTECTED_SEGMENTS = ['dashboard', 'community']

type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (
    callback: (...args: unknown[]) => void,
    options?: { timeout: number }
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

function deferNonCriticalWebWork(callback: () => void): () => void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return () => {}
  }

  const win = window as WindowWithIdleCallback
  let idleHandle: number | null = null
  let timeoutHandle: number | null = null
  let hasRun = false

  const removeListeners = () => {
    window.removeEventListener('pointerdown', run)
    window.removeEventListener('touchstart', run)
    window.removeEventListener('wheel', run)
    window.removeEventListener('keydown', run)
  }

  const run = () => {
    if (hasRun) return
    hasRun = true

    removeListeners()
    if (idleHandle !== null) {
      win.cancelIdleCallback?.(idleHandle)
      idleHandle = null
    }
    if (timeoutHandle !== null) {
      window.clearTimeout(timeoutHandle)
      timeoutHandle = null
    }

    callback()
  }

  window.addEventListener('pointerdown', run, { once: true, passive: true })
  window.addEventListener('touchstart', run, { once: true, passive: true })
  window.addEventListener('wheel', run, { once: true, passive: true })
  window.addEventListener('keydown', run, { once: true })

  if (win.requestIdleCallback) {
    idleHandle = win.requestIdleCallback(run, { timeout: 2500 })
  } else {
    timeoutHandle = window.setTimeout(run, 1800)
  }

  return () => {
    removeListeners()
    if (idleHandle !== null) {
      win.cancelIdleCallback?.(idleHandle)
    }
    if (timeoutHandle !== null) {
      window.clearTimeout(timeoutHandle)
    }
  }
}

function RootStack() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { capture } = useAnalytics()
  const segments = useSegments()
  const pathname = usePathname()
  const router = useRouter()
  const trackedPathRef = useRef<string | null>(null)
  const _colors = useThemeColors()

  const inProtected = PROTECTED_SEGMENTS.includes(segments[0] as string)

  useEffect(() => {
    if (isLoading) return
    if (inProtected && !isAuthenticated) {
      router.replace('/auth')
      return
    }
    if (
      inProtected &&
      isAuthenticated &&
      user?.application_status !== 'approved' &&
      segments[0] !== 'community'
    ) {
      router.replace('/apply/confirmation')
      return
    }
    const publicSegments = ['', 'onboarding']
    const inPublic = publicSegments.includes(segments[0] as string)
    if (inPublic && isAuthenticated) {
      router.replace('/dashboard')
    }
  }, [isAuthenticated, isLoading, inProtected, user?.application_status, segments, router])

  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (isLoading) return
    if (!pathname) return
    if (trackedPathRef.current === pathname) return

    trackedPathRef.current = pathname
    capture('route_viewed', {
      route: pathname,
      auth_state: isAuthenticated ? 'authenticated' : 'anonymous',
      application_status: user?.application_status ?? 'unknown',
    })
  }, [capture, isAuthenticated, isLoading, pathname, user?.application_status])

  // Block rendering protected screens entirely until auth state is known.
  // This prevents showing protected content for even a single frame.
  if (isLoading && inProtected) {
    return <View style={{ flex: 1, backgroundColor: 'transparent' }} />
  }

  return (
    <Stack screenOptions={{ contentStyle: { backgroundColor: 'transparent' } }}>
      <Stack.Screen name="index" options={{ headerShown: false }} />
      <Stack.Screen name="onboarding" options={{ headerShown: false }} />
      <Stack.Screen name="auth" options={{ headerShown: false }} />
      <Stack.Screen name="about" options={{ headerShown: false }} />
      <Stack.Screen name="privacy" options={{ headerShown: false }} />
      <Stack.Screen name="dashboard" options={{ headerShown: false }} />
      <Stack.Screen name="community" options={{ headerShown: false }} />
      <Stack.Screen name="campaign" options={{ headerShown: false }} />
      <Stack.Screen name="apply" options={{ headerShown: false }} />
      <Stack.Screen name="settings/index" options={{ headerShown: false, presentation: 'modal' }} />
      <Stack.Screen name="settings/profile" options={{ headerShown: false }} />
      <Stack.Screen name="hq" options={{ headerShown: false }} />
      <Stack.Screen name="unsubscribe" options={{ headerShown: false }} />
    </Stack>
  )
}

function ThemedRoot() {
  const _colors = useThemeColors()
  const { locale } = useI18n()

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  return (
    <AuthProvider>
      <View style={{ flex: 1, backgroundColor: 'transparent' }}>
        <RootStack />
      </View>
    </AuthProvider>
  )
}

export default function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    Outfit: Outfit_400Regular,
    'Outfit-Medium': Outfit_500Medium,
    'Outfit-SemiBold': Outfit_600SemiBold,
    'Outfit-Bold': Outfit_700Bold,
    'Outfit-ExtraBold': Outfit_800ExtraBold,
  })

  useEffect(() => {
    if (fontError) {
      reportError(fontError, { context: 'font_load_failed' })
    }
  }, [fontError])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const GA_ID = process.env.EXPO_PUBLIC_GA_MEASUREMENT_ID
    if (!GA_ID) return

    return deferNonCriticalWebWork(() => {
      const script1 = document.createElement('script')
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
      script1.async = true
      script1.defer = true
      document.head.appendChild(script1)

      const script2 = document.createElement('script')
      script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_ID}');
    `
      document.head.appendChild(script2)
    })
  }, [])

  // Initialize posthog-js specifically for web session replay.
  // The reason we need this in addition to PostHogProvider below is that
  // posthog-react-native has no knowledge of the browser DOM — it can't
  // inject rrweb (the library that records DOM mutations for session replay).
  // posthog-js handles that automatically when running in a browser context.
  // The useEffect must be placed BEFORE the early return below, because React's
  // rules of hooks require hooks to always be called on every render, even when
  // fonts haven't loaded yet.
  // Set the app element for modal-react-native-web (used by CountryPicker and
  // other modal components) so screen readers work correctly on web.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const Modal = require('modal-react-native-web')
    Modal.setAppElement(document.body)
  }, [])

  const isProductionAppEnv = process.env.EXPO_PUBLIC_APP_ENV === 'production'
  const isTurbinagoWebHost =
    Platform.OS !== 'web' ||
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'turbinago.com' ||
        window.location.hostname === 'www.turbinago.com'))
  const enablePostHog = isProductionAppEnv && isTurbinagoWebHost

  useEffect(() => {
    // Enable only for production deployments on turbinago.com.
    if (!enablePostHog) return
    if (Platform.OS !== 'web') return

    const origConsoleError = console.error.bind(console)
    let isReportingReentry = false

    console.error = (...args: unknown[]) => {
      origConsoleError(...args)
      if (isReportingReentry) return
      isReportingReentry = true
      try {
        const err = args.find((a) => a instanceof Error)
        if (err) {
          const ctx = args.filter((a): a is string => typeof a === 'string').join(' | ')
          reportError(err as Error, ctx ? { console_context: ctx } : undefined)
        }
      } finally {
        isReportingReentry = false
      }
    }

    void getPostHogClient()

    return () => {
      console.error = origConsoleError
    }
  }, [enablePostHog])

  const appReady = fontsLoaded

  useEffect(() => {
    if (appReady) {
      SplashScreen.hideAsync()
    }
  }, [appReady])

  if (!appReady) return null

  const disablePostHog = !enablePostHog
  const appTree = (
    <ThemeProvider>
      <I18nProvider>
        <ThemedRoot />
      </I18nProvider>
    </ThemeProvider>
  )

  return (
    <AppErrorBoundary>
      <PostHogNativeProvider disabled={disablePostHog}>{appTree}</PostHogNativeProvider>
    </AppErrorBoundary>
  )
}
